import type { FilterFormValues, TCandidateOrigin } from '@vlabs/monokit/entities/matcher/types/types';
import { permissions } from '@vlabs/shared/config';
import { ButtonSwitch, ButtonSwitchGroup, Control } from '@vlabs/uikit';
import { useFormContext } from 'react-hook-form';

import st from './Filters.module.sass';
import { EventFilterForm, FaceFilterForm } from './forms';
import { useSearchPageTranslation } from '../../i18n';
import { defaultFilterValues } from '../../model/defaultFormValues';
import { useSearch } from '../search-provider/SearchProvider';

interface FiltersProps {
  includedAttributes?: string[];
  can: (permissions: string[], params?: Record<string, string>) => boolean;
}

function Filters(
  { includedAttributes = [], can }: FiltersProps,
): JSX.Element {
  const { t } = useSearchPageTranslation();
  const formMethods = useFormContext<FilterFormValues>();
  const {
    references,
    matcherType,
    resetSearchState,
    setCandidateOrigin,
    candidateOrigin,
    search,
    setSearchResults,
  } = useSearch();

  const { reset, handleSubmit, formState } = formMethods;

  const canSearchBodies = can([permissions.event.view, permissions.event.matching], { method: 'allof' });

  const onResetFilters = () => {
    resetSearchState();
    reset(defaultFilterValues);
  };

  const onChangeCandidateOrigin = (value: TCandidateOrigin) => {
    setSearchResults(undefined);
    reset(defaultFilterValues);
    setCandidateOrigin(value);
  };

  const onSubmit = (values: FilterFormValues) => {
    setSearchResults(undefined);
    search(values);
  };

  return (
    <div className={st.FiltersPanel}>
      <div className={st.FiltersTitle}>{t('filters.title')}</div>
      <ButtonSwitchGroup
        className={st.ButtonSwitch}
        onChange={onChangeCandidateOrigin}
        value={candidateOrigin}
        variant="square"
      >
        <ButtonSwitch
          className="Subtitle-2"
          value="events"
        >
          {t('filters.buttons.candidate_origin_button_group.events')}
        </ButtonSwitch>
        <ButtonSwitch
          className="Subtitle-2"
          value="faces"
        >
          {t('filters.buttons.candidate_origin_button_group.faces')}
        </ButtonSwitch>
      </ButtonSwitchGroup>
      <div className={st.FiltersSection}>
        <form id="searchFilterForm">
          <div className={st.Divider} />
          {candidateOrigin === 'faces' && <FaceFilterForm />}
          {candidateOrigin === 'events' && <EventFilterForm includedAttributes={includedAttributes} />}
          <div className={st.Divider} />
        </form>
      </div>
      <div className={st.BottomButtonPanel}>
        <Control.Button
          data-testid="button.onFilter"
          disabled={
            (matcherType === 'body' && !canSearchBodies) || !(references?.length > 0)
          }
          onClick={handleSubmit(onSubmit)}
        >
          {t('filters.buttons.search_button.label')}
        </Control.Button>
        <Control.Button
          data-testid="button.onReset"
          disabled={!formState.isDirty}
          kind="negative"
          onClick={onResetFilters}
          type="button"
        >
          {t('filters.buttons.reset_button.label')}
        </Control.Button>
      </div>
    </div>
  );
}

export { Filters };
